import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import SweetAlert from "react-bootstrap-sweetalert";
import tmdbApi from "../../../api/tmdbApi";
import Header from "../../../components/dashboard/header";
import Footer from "../../../components/dashboard/footer";
import SideMenu from "../../student/SideMenu";
import Loader from "../../../components/loader";
import SessionPopup from "../../SessionPopup";
import { contentContext } from "../../../context/contentContext";
import deepZoom from "../../../assets/images/icons/Dna-dz.svg";

let { lambda, appname } = window.app;

export default function DoctorBiopsyEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const {
        route, setRoute,
        setCurrentPage, setRowsPerPage,
        setActiveMenuObj, setActiveMenuId,
        usePrevious,
        GetTimeActivity,
    } = useContext(contentContext);

    // ── Form state ─────────────────────────────────────────────────────────
    const [caseData, setCaseData] = useState({
        pathrefno: "",
        biopsydate: "",
        patientname: "",
        age: "",
        gender: "",
        clinicregistrationnumber: "",
        specimentype: "",
        siteofbiopsy: "",
        clinicalhistory: "",
        biopsyfiles: [],
    });
    const [selectedBiopsyType, setSelectedBiopsyType] = useState(null);
    const [biopsyTypeOptions, setBiopsyTypeOptions] = useState([]);
    const [defaultTestName, setDefaultTestName] = useState("");
    const [defaultTestCost, setDefaultTestCost] = useState("");

    // ── additional tests approval ────────────────────────────────────────────
    const [additionalTests, setAdditionalTests] = useState([]);
    const [additionaltestapprove, setAdditionaltestapprove] = useState(false);

    // ── Clinical image state ───────────────────────────────────────────────
    const [clinicalImageFiles, setClinicalImageFiles] = useState([]);
    const [clinicalImageUrls, setClinicalImageUrls] = useState(null);
    const [clinicalImageIndex, setClinicalImageIndex] = useState(0);
    const [imageCloudfront, setImageCloudfront] = useState("");
    const [showImagePopup, setShowImagePopup] = useState(false);
    const [popupImageUrl, setPopupImageUrl] = useState(null);
    const [fileSizeError, setFileSizeError] = useState(false);

    // ── UI state ───────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(true);
    const [showSessionPopup, setShowSessionPopup] = useState(false);
    const [invalidContent, setInvalidContent] = useState(false);
    const [updateSuccessPopup, setUpdateSuccessPopup] = useState(false);

    // ── Validation errors ──────────────────────────────────────────────────
    const [errors, setErrors] = useState({});

    const prevRoute = usePrevious(route);
    useEffect(() => {
        if (prevRoute !== undefined && prevRoute !== route) {
            setCurrentPage(1);
            setRowsPerPage(15);
        }
    }, [prevRoute]);

    useEffect(() => {
        if (window.site?.common?.imageCloudfront) {
            setImageCloudfront(window.site.common.imageCloudfront);
        } else {
            setTimeout(() => {
                if (window.site?.common?.imageCloudfront) {
                    setImageCloudfront(window.site.common.imageCloudfront);
                }
            }, 1000);
        }
    }, [window.site]);

    useEffect(() => {
        if (!token) { navigate("/"); return; }
        setActiveMenuId(200005);
        setActiveMenuObj({ "Client Management": false, "Contact Management": false, "Reports": false });
        setRoute("user");
        fetchLookupOptions("Biopsy");
        fetchCaseForEdit();
    }, []);

    const fetchLookupOptions = async (type) => {
        const res = await tmdbApi.lookupInfo(type);
        if (res?.result?.length > 0) {
            if (type === "Biopsy") setBiopsyTypeOptions(res.result);
        }
    };

    const fetchCaseForEdit = (attempt = 0) => {
        GetTimeActivity();
        setIsLoading(true);
        axios.get(`${lambda}/biopsyCaseInfo?appname=${appname}&patientid=${id}&token=${token}`)
            .then((res) => {
                if (res?.data?.result === "Invalid token or Expired") {
                    setShowSessionPopup(true);
                    setIsLoading(false);
                } else if (res?.data?.result?.length > 0) {
                    const existing = res.data.result[0];
                    setCaseData({
                        pathrefno: existing.pathrefno || "",
                        biopsydate: existing.biopsydate || "",
                        patientname: existing.patientname || "",
                        age: existing.age || "",
                        gender: existing.gender || "",
                        clinicregistrationnumber: existing.clinicregistrationnumber || "",
                        specimentype: existing.specimentype || "",
                        siteofbiopsy: existing.siteofbiopsy || "",
                        clinicalhistory: existing.clinicalhistory || "",
                        biopsyfiles: existing.biopsyfiles || [],
                    });
                    setSelectedBiopsyType(existing.biopsyType || null);
                    setDefaultTestName(existing.defaultBiopsyTest?.name || "");
                    setDefaultTestCost(existing.defaultBiopsyTest?.cost || "");
                    setAdditionalTests(existing.additionalTests || []);
                    setAdditionaltestapprove(!!existing.additionaltestapprove);
                    setIsLoading(false);
                } else if (attempt < 3) {
                    // just-created cases can briefly 404 while the write settles — retry a few times
                    setTimeout(() => fetchCaseForEdit(attempt + 1), 800);
                } else {
                    setInvalidContent(true);
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (attempt < 3) {
                    setTimeout(() => fetchCaseForEdit(attempt + 1), 800);
                } else {
                    setIsLoading(false);
                }
            });
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setCaseData((prev) => ({ ...prev, [name]: value }));
    };

    const handleBiopsyTypeChange = (selected) => {
        setSelectedBiopsyType(selected);
        setCaseData((prev) => ({ ...prev, biopsyType: selected }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!caseData.patientname?.trim()) newErrors.patientname = "Please enter Patient Name";
        if (!caseData.clinicregistrationnumber?.trim()) newErrors.clinicregistrationnumber = "Please enter Clinic Registration Number";
        if (!caseData.specimentype?.trim()) newErrors.specimentype = "Please enter Specimen Type";
        if (!caseData.siteofbiopsy?.trim()) newErrors.siteofbiopsy = "Please enter Site of Biopsy";
        if (!caseData.clinicalhistory?.trim()) newErrors.clinicalhistory = "Please enter Clinical History";
        if (!selectedBiopsyType || (Array.isArray(selectedBiopsyType) && selectedBiopsyType.length === 0)) {
            newErrors.biopsyType = "Please select Biopsy Type";
        }
        setErrors(newErrors);
        setTimeout(() => setErrors({}), 3000);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        GetTimeActivity();
        if (!validateForm()) return;
        updateCase();
    };

    const updateCase = async () => {
        setIsLoading(true);

        const newFiles = clinicalImageFiles.length > 0
            ? await uploadClinicalImages(clinicalImageFiles, id)
            : [];
        const biopsyfiles = [...(caseData?.biopsyfiles || []), ...newFiles];

        const payload = {
            ...caseData,
            biopsyType: selectedBiopsyType,
            caseType: "Biopsy",
            biopsyfiles,
            defaultBiopsyTest: {
                name: defaultTestName || "Biopsy Test",
                cost: defaultTestCost,
                isDefault: true,
            },
            additionaltestapprove,
        };
        if (additionaltestapprove) {
            payload.status = "Additional Test In Progress";
        }
        axios.post(`${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}`, payload)
            .then((res) => {
                if (res?.result === "Invalid token or Expired") {
                    setShowSessionPopup(true);
                } else {
                    setUpdateSuccessPopup(true);
                }
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    };

    const uploadClinicalImages = async (files, biopsyId) => {
        const bucket = window.site?.common?.resourceBucket;
        const uploadedFiles = await Promise.all(
            Array.from(files).map(async (file) => {
                const timestamp = Date.now();
                const ext = file.name.split(".").pop();
                const ObjectID = require("bson-objectid");
                const uniqueId = new ObjectID().toString();
                const filename = `${uniqueId}_image_${timestamp}.${ext}`;
                const uploadPath = `${appname}/${biopsyId}/${uniqueId}/${filename}`;
                const data = { source_bucket: bucket, sourcepath: uploadPath };
                try {
                    const signedRes = await axios.post(
                        `${lambda}/uploadFiles?appname=${appname}&biopsyid=${biopsyId}`,
                        data,
                        { headers: { "Content-Type": "application/json" } }
                    );
                    if (signedRes?.data?.result) {
                        await axios.put(signedRes.data.result, file, {
                            headers: { "Content-Type": file.type, "Cache-Control": "no-cache" },
                        });
                        return { fileid: uniqueId, filename, sourcepath: uploadPath };
                    }
                    return null;
                } catch (err) {
                    console.error("Image upload error:", err);
                    return null;
                }
            })
        );
        return uploadedFiles.filter(Boolean);
    };

    const handleClinicalImageSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const maxSize = 1048612 * 20;
        selectedFiles.forEach((file, index) => {
            if (file.size < maxSize) {
                const adjustedIndex = clinicalImageIndex + index;
                const blobUrl = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    setClinicalImageFiles((prev) => [...prev, file]);
                    setClinicalImageUrls((prev) => ({
                        ...prev,
                        [`blob${adjustedIndex}`]: { url: blobUrl, name: file.name },
                    }));
                    setClinicalImageIndex(adjustedIndex + 1);
                };
                img.src = blobUrl;
            } else {
                setFileSizeError(true);
                setTimeout(() => setFileSizeError(false), 3000);
            }
        });
    };

    const handleClinicalImageRemove = (key) => {
        const urls = { ...clinicalImageUrls };
        setClinicalImageFiles(clinicalImageFiles.filter((f) => f.name !== urls[key]?.name));
        delete urls[key];
        setClinicalImageUrls(urls);
        setClinicalImageIndex(clinicalImageIndex - 1);
    };

    const handleRemoveExistingFile = (fileId) => {
        setCaseData((prev) => ({
            ...prev,
            biopsyfiles: (prev.biopsyfiles || []).filter((f) => f.fileid !== fileId),
        }));
    };

    const handleImagePopup = (imageUrl) => {
        setShowImagePopup(true);
        setPopupImageUrl(imageUrl);
    };

    const handleBack = () => {
        GetTimeActivity();
        navigate("/biopsy/cases");
    };

    const onUpdateSuccess = () => {
        setUpdateSuccessPopup(false);
    };

    return (
        <>
            {showSessionPopup && <SessionPopup />}

            <SweetAlert
                show={invalidContent}
                custom
                confirmBtnText="Ok"
                confirmBtnBsStyle="primary"
                title="Case Not Found"
                onConfirm={() => { setInvalidContent(false); navigate("/biopsy/cases"); }}
            />
            <SweetAlert
                show={updateSuccessPopup}
                custom
                confirmBtnText="Ok"
                confirmBtnBsStyle="primary"
                title="Case updated successfully"
                onConfirm={onUpdateSuccess}
            />

            <div id="layout-wrapper">
                <Header />
                <SideMenu />

                {!invalidContent && (
                    <div className="main-content user-management create-user" id="main-content">
                        <div className="page-content">
                            <div className="container-fluid">
                                <div className="row mb-4 breadcrumb">
                                    <div className="col-lg-12">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <h4 className="mb-2 card-title">EDIT CASE</h4>
                                                <p className="menu-path">
                                                    <span>BIOPSY</span> / <b>EDIT</b>
                                                </p>
                                            </div>
                                            <a onClick={handleBack} className="create-quiz-btn border-0" style={{ cursor: "pointer" }}>
                                                Back
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <div className="row table-data">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-body cpc-management case-info edit-cpc">
                                                {isLoading ? <Loader /> : (
                                                    <div className="create-user-block">
                                                        <div className="form-block">
                                                            <div className="row col-lg-12">

                                                                {/* PATH REF NO — not editable */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">PATH REF NO</label>
                                                                        <input
                                                                            name="pathrefno"
                                                                            type="text"
                                                                            className="form-control"
                                                                            value={caseData.pathrefno}
                                                                            disabled
                                                                            readOnly
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* BIOPSY DATE — not editable */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">BIOPSY DATE</label>
                                                                        <input
                                                                            name="biopsydate"
                                                                            type="date"
                                                                            className="form-control"
                                                                            value={caseData.biopsydate}
                                                                            disabled
                                                                            readOnly
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* CLINIC REGISTRATION NUMBER */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">CLINIC REGISTRATION NUMBER <span className="required">*</span></label>
                                                                        <input
                                                                            name="clinicregistrationnumber"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Clinic Registration Number"
                                                                            value={caseData.clinicregistrationnumber}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.clinicregistrationnumber && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.clinicregistrationnumber}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* PATIENT NAME */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">PATIENT NAME <span className="required">*</span></label>
                                                                        <input
                                                                            name="patientname"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Patient Name"
                                                                            value={caseData.patientname}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.patientname && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.patientname}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* AGE */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">AGE</label>
                                                                        <input
                                                                            name="age"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Age"
                                                                            value={caseData.age}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* GENDER */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">GENDER</label>
                                                                        <select
                                                                            name="gender"
                                                                            className="form-control form-select"
                                                                            value={caseData.gender}
                                                                            onChange={handleFieldChange}
                                                                        >
                                                                            <option value="">Select</option>
                                                                            <option value="Male">Male</option>
                                                                            <option value="Female">Female</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                {/* SPECIMEN TYPE */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">SPECIMEN TYPE <span className="required">*</span></label>
                                                                        <input
                                                                            name="specimentype"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Specimen Type"
                                                                            value={caseData.specimentype}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.specimentype && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.specimentype}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* SITE OF BIOPSY */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">SITE OF BIOPSY <span className="required">*</span></label>
                                                                        <input
                                                                            name="siteofbiopsy"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Site of Biopsy"
                                                                            value={caseData.siteofbiopsy}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.siteofbiopsy && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.siteofbiopsy}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* BIOPSY TYPE */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">BIOPSY TYPE <span className="required">*</span></label>
                                                                        <Select
                                                                            isMulti={false}
                                                                            placeholder="Select Biopsy Type"
                                                                            value={selectedBiopsyType}
                                                                            onChange={handleBiopsyTypeChange}
                                                                            options={biopsyTypeOptions.map((item) => ({
                                                                                value: item.lookupcode,
                                                                                label: item.name,
                                                                            }))}
                                                                            isClearable
                                                                        />
                                                                        {errors.biopsyType && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.biopsyType}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* TESTS / SLIDES — read-only */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">TEST NAME</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            readOnly
                                                                            value={defaultTestName}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">COST (₹)</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            readOnly
                                                                            value={defaultTestCost}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* CLINICAL HISTORY */}
                                                                <div className="col-lg-12 col-md-12 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">CLINICAL HISTORY <span className="required">*</span></label>
                                                                        <textarea
                                                                            name="clinicalhistory"
                                                                            className="form-control edit-cpc-textarea"
                                                                            placeholder="Enter Clinical History"
                                                                            rows={12}
                                                                            style={{ height: "200px" }}
                                                                            cols={6}
                                                                            value={caseData.clinicalhistory}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.clinicalhistory && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.clinicalhistory}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* CLINICAL IMAGES */}
                                                                <div className="col-md-12">
                                                                    <p className="mt-0 mb-0 text-muted col-8 file-format">Note: File size: 20 MB | Supported Formats: JPG, PNG</p>
                                                                    {fileSizeError && <p className="text-danger small">File size exceeds 20MB limit.</p>}
                                                                    <div className="d-flex justify-content-start col-lg-12 clinical-photo-section gap-3 mt-3">
                                                                        <div className="uploaded_images-grid mb-3 mt-2">
                                                                            <div className="upload-box">
                                                                                <div className="file_upload add-box">
                                                                                    <input
                                                                                        type="file"
                                                                                        id="clinical-image-upload"
                                                                                        accept="image/jpeg, image/png"
                                                                                        multiple
                                                                                        className="file-input"
                                                                                        onChange={handleClinicalImageSelect}
                                                                                        style={{ display: "none" }}
                                                                                    />
                                                                                    <label htmlFor="clinical-image-upload" className="Add-btn d-flex align-items-center justify-content-center flex-column">
                                                                                        <p className="mb-0">
                                                                                            <span className="material-symbols-outlined plus-icon">add</span>
                                                                                        </p>
                                                                                        ADD
                                                                                    </label>
                                                                                </div>
                                                                            </div>

                                                                            {/* newly selected (blob preview) */}
                                                                            {clinicalImageUrls && Object.keys(clinicalImageUrls).map((key) => (
                                                                                <div className="thumb-box" key={key}>
                                                                                    <div
                                                                                        className="thumb-img cursor-pointer"
                                                                                        onClick={() => handleImagePopup(clinicalImageUrls[key].url)}
                                                                                    >
                                                                                        <img src={clinicalImageUrls[key].url} alt="clinical" />
                                                                                        <span
                                                                                            className="icon-close"
                                                                                            onClick={(e) => { e.stopPropagation(); handleClinicalImageRemove(key); }}
                                                                                        >×</span>
                                                                                    </div>
                                                                                    <p className="thumb-name text-truncate small mb-0">{clinicalImageUrls[key].name}</p>
                                                                                </div>
                                                                            ))}

                                                                            {/* existing saved photos */}
                                                                            {caseData?.biopsyfiles?.map((item, key) => {
                                                                                const imgPath = `${imageCloudfront}${item?.sourcepath?.replace(/ /g, "%20")}?auto=compress,format`;
                                                                                return (
                                                                                    <div className="thumb-box" key={key}>
                                                                                        <div className="thumb-img cursor-pointer" onClick={() => handleImagePopup(imgPath)}>
                                                                                            <img
                                                                                                src={`${imgPath}&width=150`}
                                                                                                alt="clinical"
                                                                                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = deepZoom; }}
                                                                                            />
                                                                                            <span
                                                                                                className="icon-close"
                                                                                                onClick={(e) => { e.stopPropagation(); handleRemoveExistingFile(item.fileid); }}
                                                                                            >×</span>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* ── ADDITIONAL TESTS APPROVAL ──────── */}
                                                                {additionalTests.length > 0 && (
                                                                    <div className="col-lg-8 col-md-12 col-sm-12 mt-2">
                                                                        <label className="form_label">ADDITIONAL TESTS REQUIRED</label>
                                                                        <table className="table table-bordered">
                                                                            <thead style={{ background: "#f8f9fa" }}>
                                                                                <tr>
                                                                                    <th style={{ fontWeight: "600", fontSize: "13px" }}>Test Name</th>
                                                                                    <th style={{ fontWeight: "600", fontSize: "13px" }}>Fee (₹)</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {additionalTests.map((t, index) => (
                                                                                    <tr key={`${t.name}-${index}`}>
                                                                                        <td style={{ fontSize: "13px" }}>{t.name}</td>
                                                                                        <td style={{ fontSize: "13px" }}>{t.fee}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                            <tfoot>
                                                                                <tr style={{ background: "#f8f9fa" }}>
                                                                                    <td style={{ fontWeight: "700", fontSize: "13px" }}>Total Amount</td>
                                                                                    <td style={{ fontWeight: "700", fontSize: "13px" }}>
                                                                                        ₹ {additionalTests.reduce((sum, t) => sum + (parseFloat(t.fee) || 0), 0)}
                                                                                    </td>
                                                                                </tr>
                                                                            </tfoot>
                                                                        </table>

                                                                        <div className="form-check mt-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                id="additionaltestapprove"
                                                                                checked={additionaltestapprove}
                                                                                onChange={(e) => setAdditionaltestapprove(e.target.checked)}
                                                                            />
                                                                            <label className="form-check-label" htmlFor="additionaltestapprove">
                                                                                Please Approve Additional Tests To Get The Report
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* SUBMIT */}
                                                                <div className="col-md-12 mt-3">
                                                                    <button
                                                                        className="create-quiz-btn border-0"
                                                                        type="button"
                                                                        onClick={handleSubmit}
                                                                    >
                                                                        Update
                                                                    </button>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Footer />
                    </div>
                )}
            </div>

            {/* Image popup lightbox */}
            {showImagePopup && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                        backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1003,
                        display: "flex", alignItems: "start", justifyContent: "center", padding: "20px",
                    }}
                    onClick={() => setShowImagePopup(false)}
                >
                    <div
                        style={{ position: "relative", width: "auto", height: "100%", padding: "20px", display: "flex", justifyContent: "center", alignItems: "start" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img src={popupImageUrl} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
                        <button
                            onClick={() => setShowImagePopup(false)}
                            style={{ position: "absolute", top: "10px", right: "10px", background: "white", border: "none", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", fontWeight: "bold" }}
                        >×</button>
                    </div>
                </div>
            )}
        </>
    );
}
